import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Router } from '@angular/router';
import { DividerComponent } from '@shared/components/divider.component';
import { TitleContainerComponent } from '@shared/components/title-container.component';
import { TreatyApiService } from '../services/treaty-api.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { concat, finalize, of, startWith, switchMap, tap } from 'rxjs';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { treatiesColDefs, treatiesDefaultColDef, TREATY_PAGE_SIZE } from '../treaty-table.config';
import { AgGridAngular } from 'ag-grid-angular';
import { quickFilterParser } from '@shared/utils/quick-filter-parser.util';
import { customTheme } from 'src/styles/grid-table/grid-table-theme';
import { RowClickedEvent } from 'ag-grid-community';
import { IsRiskManagerDirective } from '@core/directives/is-risk-manager.directive';

@Component({
  selector: 'app-all-treaties',
  imports: [
    TitleContainerComponent,
    MatButtonModule,
    MatIconModule,
    DividerComponent,
    MatProgressBarModule,
    ReactiveFormsModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatInputModule,
    AgGridAngular,
    IsRiskManagerDirective,
  ],
  templateUrl: './all-treaties.component.html',
  styleUrl: './all-treaties.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AllTreatiesComponent {
  readonly pageSize = TREATY_PAGE_SIZE;
  readonly theme = customTheme;
  readonly router = inject(Router);
  readonly treatyApiService = inject(TreatyApiService);

  isLoading = signal(false);
  myScopeControl = new FormControl(true, { nonNullable: true });
  keywordControl = new FormControl('', { nonNullable: true });

  treaties = toSignal(
    this.myScopeControl.valueChanges.pipe(
      startWith(this.myScopeControl.value),
      tap(() => this.isLoading.set(true)),
      switchMap(value =>
        concat(
          of([]),
          this.treatyApiService.getAllTreaties$(value).pipe(
            finalize(() => {
              this.isLoading.set(false);
            })
          )
        )
      )
    ),
    { initialValue: [] }
  );

  textFilter = toSignal(this.keywordControl.valueChanges, { initialValue: '' });
  quickFilterParser = quickFilterParser;

  defaultColDef = treatiesDefaultColDef;
  colDef = treatiesColDefs;

  newTreaty() {
    this.router.navigate(['/treaty-assessments/new']);
  }

  onRowClicked(event: RowClickedEvent) {
    const treatyRow = event.data;
    if (treatyRow) {
      if (treatyRow.isProperty) {
        this.router.navigate(['treaty-assessments/detail/property', treatyRow.treatyId]);
      } else if (treatyRow.isCasualty) {
        this.router.navigate(['treaty-assessments/detail/casualty', treatyRow.treatyId]);
      }
    }
  }
}
